// Enregistrement du Service Worker SAGeCom (PWA)
// Inspiré du template CRA PWA — adapté au basename /gestioncaisse

const isLocalhost = Boolean(
  typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '[::1]' ||
      window.location.hostname.match(/^127(?:\.\d{1,3}){3}$/))
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config): void {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return;
  }

  const publicUrl = new URL(
    process.env.PUBLIC_URL || '/',
    window.location.href
  );
  if (publicUrl.origin !== window.location.origin) {
    return;
  }

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL || ''}/service-worker.js`;

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config);
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

// Recharge la page une seule fois quand le nouveau SW prend le contrôle
let refreshing = false;
function listenForControllerChange(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
}

function registerValidSW(swUrl: string, config?: Config): void {
  listenForControllerChange();

  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      // Cas 1 : un SW mis à jour attend déjà (visite précédente) → propose tout de suite
      if (registration.waiting && navigator.serviceWorker.controller) {
        if (config && config.onUpdate) config.onUpdate(registration);
      }

      // Cas 2 : une nouvelle version s'installe pendant la session
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              if (config && config.onUpdate) config.onUpdate(registration);
            } else if (config && config.onSuccess) {
              config.onSuccess(registration);
            }
          }
        };
      };

      // Vérifie proactivement les mises à jour : au retour sur l'onglet + périodiquement
      const checkForUpdate = () => registration.update().catch(() => undefined);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });
      window.setInterval(checkForUpdate, 60 * 1000);
    })
    .catch((error) => {
      console.error('[SW] Erreur enregistrement service worker :', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config): void {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      // Pas de connexion : reste sur la version cachée
    });
}

// En DÉVELOPPEMENT, l'app n'utilise aucune PWA. Mais un service worker laissé
// par un ancien build de PROD servi sur le même port continue d'intercepter et
// de servir un bundle PÉRIMÉ (symptômes : modale figée, saisie morte, valeurs
// par défaut absentes). On purge donc tout SW + cache résiduel au démarrage dev.
export function cleanupDevServiceWorkers(): void {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return;
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((rs) => rs.forEach((r) => r.unregister()))
      .catch(() => undefined);
  }
  if (window.caches && typeof caches.keys === 'function') {
    caches
      .keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => undefined);
  }
}

// Désenregistre le service worker général de l'application. Celui du terrain
// GMAO (portée « …/gmao/terrain », public/sw-terrain.js) est conservé : il ne
// contrôle que cette page et permet de l'ouvrir sans réseau. Attendre
// `serviceWorker.ready` le supprimait à chaque ouverture de la page Terrain,
// dont il est justement le service worker.
export function unregister(): void {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs
        .filter((r) => !r.scope.replace(/\/$/, '').endsWith('/gmao/terrain'))
        .forEach((r) => r.unregister()))
      .catch((error) => console.error(error.message));
  }
}
